/**
 * Helpers for DM thread message list state (socket upsert + REST merge).
 */

export function messageIdKey(id) {
  if (id == null || id === "") return null;
  return String(id);
}

export function upsertMessage(prev, message) {
  if (!message?.message_id) return prev || [];
  const list = prev || [];
  const key = messageIdKey(message.message_id);
  if (!key) return list;
  const idx = list.findIndex((m) => messageIdKey(m.message_id) === key);
  if (idx === -1) return [...list, message];
  const next = [...list];
  next[idx] = { ...list[idx], ...message };
  return next;
}

function messageTime(m) {
  const t = m?.created_at ? new Date(m.created_at).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
}

function sortMessages(list) {
  return [...list].sort((a, b) => {
    const ta = messageTime(a);
    const tb = messageTime(b);
    if (ta !== tb) return ta - tb;
    return (Number(a.message_id) || 0) - (Number(b.message_id) || 0);
  });
}

/**
 * Apply a REST page without dropping newer socket-only messages.
 * Covers the race where listDmMessages started before a webhook save but
 * finishes after dm.message was already upserted into local state.
 */
export function mergeServerMessages(prev, serverMessages) {
  const server = Array.isArray(serverMessages) ? serverMessages : [];
  const prior = Array.isArray(prev) ? prev : [];
  if (!prior.length) return server;
  if (!server.length) return prior;

  const serverIds = new Set(
    server.map((m) => messageIdKey(m.message_id)).filter(Boolean)
  );
  const maxServerId = server.reduce(
    (max, m) => Math.max(max, Number(m.message_id) || 0),
    0
  );
  const maxServerTime = server.reduce(
    (max, m) => Math.max(max, messageTime(m)),
    0
  );

  // Prefer server copy for ids present on both; keep local-only newer rows.
  const mergedById = new Map();
  for (const m of server) {
    const key = messageIdKey(m.message_id);
    if (key) mergedById.set(key, m);
  }
  for (const m of prior) {
    const key = messageIdKey(m.message_id);
    if (!key) continue;
    if (serverIds.has(key)) continue;
    const id = Number(m.message_id) || 0;
    const t = messageTime(m);
    if (id > maxServerId || t >= maxServerTime) {
      mergedById.set(key, m);
    }
  }

  return sortMessages([...mergedById.values()]);
}
